import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { checkUser } from './AuthService';

const useAuthService = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [domains, setDomains] = useState([]);
  const [defaultDomain, setDefaultDomain] = useState(null);
  const [error, setError] = useState(null);

  // Chargement initial des données de l'utilisateur lors de la connexion
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const email = user.primaryEmailAddress.emailAddress;
        const result = await checkUser(email);
        // console.log("Données récupérées depuis checkUser :", result);
        setUserData(result);
        setDomains(result.domains);
        setDefaultDomain(result.domains.find((domain) => domain.isDefault) || null);
      } catch (err) {
        setError(err);
        toast.error("Erreur lors de la récupération des informations de l'utilisateur.");
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchUserData();
    }
  }, [user]);

  // Mise à jour du storage à chaque changement de userData
  useEffect(() => {
    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
    }
  }, [userData]);

  // Mise à jour du storage à chaque changement des domains
  useEffect(() => {
    if (domains) {
      localStorage.setItem('domains', JSON.stringify(domains));
    }
  }, [domains]);

  // Mise à jour du storage à chaque changement du domaine par défaut
  useEffect(() => {
    if (defaultDomain) {
      localStorage.setItem('defaultDomain', JSON.stringify(defaultDomain));
    }
  }, [defaultDomain]);

  // Fonction pour récupérer et mettre à jour les rôles dynamiquement
  const fetchRoles = async () => {
    if (domains.length > 0) {
      try {
        const email = user.primaryEmailAddress.emailAddress;
        const result = await checkUser(email);
        const updatedDomains = domains.map((domain) => {
          const matchedDomain = result.domains.find((d) => d.id === domain.id);
          return matchedDomain ? { ...domain, role: matchedDomain.role } : domain;
        });
        setDomains(updatedDomains);
        setUserData((prevUserData) => ({
          ...prevUserData,
          domains: updatedDomains
        }));
      } catch (err) {
        setError(err);
        toast.error("Erreur lors de la récupération des rôles.");
      }
    }
  };

  // Appel initial pour mettre à jour les rôles une fois le chargement des domains terminé
  useEffect(() => {
    if (domains.length > 0) {
      fetchRoles();
    }
  }, []); // Ne tourne qu'une fois au chargement initial

  return { loading, userData, domains, defaultDomain, error };
};

export default useAuthService;
